import { differenceInMinutes, isValid, parseISO } from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';

import { gettext } from '@eventbrite/i18n';
import classNames from 'classnames';

import { getDateFnsLocale } from '@eventbrite/datetime-fns';
import { Typography } from '@eventbrite/marmalade';
import type {
    TimeSlotProps,
    TimeSlotSignalProps,
} from '@eventbrite/timed-entry';
import React, { type ReactElement } from 'react';
import {
    Discount,
    DiscountType,
    SalesStatus,
    TimeSlot,
    UrgencySignal,
} from '../../../state/infiniteCalendarSeries';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../../utils/rebrand';
import { BoltSvg } from '../assests/BoltSvg';
import { FlameSvg } from '../assests/FlameSvg';
import { PopularRebrandSvg } from '../assests/PopularRebrandSvg';
import { SignalDiscountRebrandSvg } from '../assests/SignalDiscountRebrandSvg';
import { SignalDiscountSvg } from '../assests/SignalDiscountSvg';
import {
    DayPart,
    ExtendedTimeSlotProps,
    SessionsByDurationAndTimeOfDay,
    SessionsGroupedByTimeOfDay,
    TAB_HEADERS,
} from '../types';
import defaultStyles from './TimeSlot.module.scss';
import rebrandStyles from './TimeSlot.rebranding.module.scss';

type SignalConfig = {
    condition: boolean;
    icon: ReactElement;
    text: string;
};

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};

const _getStylesModule = (isAppRebrandActive: boolean) => {
    const theme = getStyles(isAppRebrandActive);
    return stylesModules[theme];
};

export function getSessionDayPart(
    startDatetime: string,
    timezone: string,
): keyof DayPart {
    if (!isValid(parseISO(startDatetime))) {
        throw new Error(
            `Invalid start datetime "${startDatetime}" passed to getSessionDayPart`,
        );
    }
    const startTime = utcToZonedTime(startDatetime, timezone);
    const startHour = startTime.getHours();
    if (startHour >= 4 && startHour < 12) return 'Morning';
    if (startHour >= 12 && startHour < 18) return 'Afternoon';
    return 'Night';
}

function isSlotSoldOut(salesStatus: SalesStatus) {
    return salesStatus === SalesStatus.soldOut;
}

function isSlotDisabled(salesStatus: SalesStatus) {
    return [SalesStatus.soldOut, SalesStatus.notOnSale].includes(salesStatus);
}

function getTimeSlotClassName(
    salesStatus: SalesStatus,
    isAppRebrandActive: boolean,
) {
    const styles = _getStylesModule(isAppRebrandActive);
    const isSoldOut = isSlotSoldOut(salesStatus);
    const isDisabled = isSlotDisabled(salesStatus);
    return classNames(styles.slot, {
        [styles.soldOutSlot]: isSoldOut,
        [styles.disabledSlot]: isDisabled && !isSoldOut,
    });
}

function getSessionTextClassName(
    salesStatus: SalesStatus,
    isAppRebrandActive: boolean,
) {
    const styles = _getStylesModule(isAppRebrandActive);
    return isSlotDisabled(salesStatus)
        ? styles.disabledSessionText
        : styles.sessionText;
}

function _createSoldOutSignal(
    isAppRebrandActive: boolean,
): TimeSlotSignalProps {
    const styles = _getStylesModule(isAppRebrandActive);
    return {
        className: styles.soldOutSignal,
        content: (
            <Typography
                variant="body-sm-bold"
                as="span"
                className={styles.soldOutSignalText}
            >
                {gettext('Sold out').toString()}
            </Typography>
        ),
    };
}

function _createDiscountSignal(
    discountValue: number,
    isAppRebrandActive: boolean,
): TimeSlotSignalProps | null {
    const styles = _getStylesModule(isAppRebrandActive);
    // TODO [EB-296073] 👇 handle discount conditions once discounts are integrated
    if (discountValue > 0) {
        return {
            className: styles.discountSignal,
            icon: isAppRebrandActive ? (
                <SignalDiscountRebrandSvg />
            ) : (
                <SignalDiscountSvg />
            ),
        };
    }
    return null;
}

function _getPrimaryUrgencySignal(
    urgencySignals: UrgencySignal,
    isAppRebrandActive: boolean,
): TimeSlotSignalProps | null {
    const styles = _getStylesModule(isAppRebrandActive);
    const signalConfigs: SignalConfig[] = [
        {
            condition: urgencySignals.goingFast,
            icon: <BoltSvg />,
            text: gettext('Going fast').toString(),
        },
        {
            condition: urgencySignals.fewTickets,
            icon: <FlameSvg />,
            text: gettext('Few tickets left').toString(),
        },
        {
            condition: urgencySignals.popular,
            icon: isAppRebrandActive ? <PopularRebrandSvg /> : <FlameSvg />,
            text: gettext('Popular').toString(),
        },
    ];
    const signal = signalConfigs.find((config) => config.condition);
    return signal
        ? {
              className: styles.primaryUrgencySignal,
              icon: signal.icon,
              content: (
                  <Typography
                      variant="body-sm-bold"
                      as="span"
                      className={styles.primaryUrgencySignalText}
                  >
                      {signal.text}
                  </Typography>
              ),
          }
        : null;
}

export function getUrgencySignals(
    session: TimeSlot,
    isAppRebrandActive: boolean,
) {
    const { urgencySignals, salesStatus, discount } = session;
    if (isSlotSoldOut(salesStatus)) {
        return [_createSoldOutSignal(isAppRebrandActive)];
    }

    const signals: TimeSlotSignalProps[] = [
        _getPrimaryUrgencySignal(urgencySignals, isAppRebrandActive),
        _createDiscountSignal(discount.discountValue, isAppRebrandActive),
    ].filter(Boolean) as TimeSlotSignalProps[];

    return signals;
}

export const formatDiscountText = (discount: Discount): string =>
    `${discount.discountValue}${
        discount.discountType === DiscountType.percentage ? '%' : ''
    } ${gettext('off')}`;

export function getOverallDiscount(timeSlots: Array<TimeSlot>) {
    const overallDiscount = timeSlots.find(
        (item) => item.discount.discountValue > 0,
    );
    return overallDiscount
        ? formatDiscountText(overallDiscount.discount)
        : undefined;
}

export function getSessionDuration(startUtc: string, endUtc: string) {
    const startDate = parseISO(startUtc);
    const endDate = parseISO(endUtc);
    return differenceInMinutes(endDate, startDate);
}

export function getDurationString(minutes: number) {
    if (minutes < 60) {
        return `${minutes} ${gettext('minutes')}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    return remainingMinutes === 0
        ? `${hours} ${hours === 1 ? gettext('hour') : gettext('hours')}`
        : `${(minutes / 60).toFixed(1)} ${gettext('hours')}`;
}

export const getEmptyDayPartMap = <T,>(): Record<keyof DayPart, T[]> => ({
    Morning: [],
    Afternoon: [],
    Night: [],
});

export const getSessionsGroupedByTimeOfDay = (
    timeSlots: readonly TimeSlot[],
    timezone: string,
) =>
    timeSlots.reduce((groups, slot) => {
        const dayPart = getSessionDayPart(slot.startDatetime, timezone);
        groups[dayPart].push(slot);
        return groups;
    }, getEmptyDayPartMap<TimeSlot>());

export const getSessionsInfoText = (
    sessions: TimeSlot[],
    locale: string,
    timezone: string,
) => {
    if (sessions.length <= 1) {
        return '';
    }
    const [firstSession] = sessions;

    if (areAllSessionsDurationEqual(sessions)) {
        const duration = getSessionDuration(
            firstSession.startDatetime,
            firstSession.endDatetime,
        );
        return gettext('Each option is %(duration)s long', {
            duration: getDurationString(duration),
        });
    }

    if (areAllSessionEndTimesEqual(sessions)) {
        const endTime = getFormattedTime(
            firstSession.endDatetime,
            locale,
            timezone,
        );
        return gettext('Entry times vary, all end at %(endTime)s', {
            endTime,
        });
    }

    return '';
};

export const areAllSessionsDurationEqual = (sessions: TimeSlot[]) => {
    if (sessions.length <= 1) {
        return false;
    }
    const durations = sessions.map((session) =>
        getSessionDuration(session.startDatetime, session.endDatetime),
    );
    const firstSessionDuration = durations[0];
    return durations.every((duration) => duration === firstSessionDuration);
};

export const areAllSessionEndTimesEqual = (sessions: TimeSlot[]) => {
    if (sessions.length <= 1) {
        return false;
    }
    const endSessionTimes = sessions.map((session) =>
        Date.parse(session.endDatetime),
    );

    const firstSessionEndTime = endSessionTimes[0];
    return endSessionTimes.every((endTime) => endTime === firstSessionEndTime);
};

const getTimeSlotProps = (
    session: TimeSlot,
    isAppRebrandActive: boolean,
    showEndTime: boolean,
): TimeSlotProps => {
    return {
        id: session.id,
        startDatetime: session.startDatetime,
        endDatetime: showEndTime ? session.endDatetime : undefined,
        urgencySignals: getUrgencySignals(session, isAppRebrandActive),
        isDisable: isSlotDisabled(session.salesStatus),
        className: getTimeSlotClassName(
            session.salesStatus,
            isAppRebrandActive,
        ),
        sessionTextClassName: getSessionTextClassName(
            session.salesStatus,
            isAppRebrandActive,
        ),
    };
};

export const mapSessionsToTimeSlotList = (
    sessions: TimeSlot[],
    isAppRebrandActive: boolean,
): TimeSlotProps[] => {
    if (!sessions.length) {
        return [];
    }
    const allSessionsHaveSameDuration = areAllSessionsDurationEqual(sessions);
    const allSessionsHaveSameEndTime = areAllSessionEndTimesEqual(sessions);
    const showEndTime =
        !allSessionsHaveSameDuration && !allSessionsHaveSameEndTime;
    return sessions.map((session) =>
        getTimeSlotProps(session, isAppRebrandActive, showEndTime),
    );
};

export function getSessionsByDurationAndTimeOfDay({
    timeSlots,
    timezone,
    isAppRebrandActive,
}: {
    timeSlots: TimeSlot[];
    timezone: string;
    locale: string;
    isAppRebrandActive: boolean;
}) {
    const sessionsByDuration: Record<number, ExtendedTimeSlotProps[]> = {};
    const sessionsByDurationAndTimeOfDay: SessionsByDurationAndTimeOfDay = {};

    for (const session of timeSlots) {
        const duration = getSessionDuration(
            session.startDatetime,
            session.endDatetime,
        );
        const dayPart = getSessionDayPart(session.startDatetime, timezone);

        const props: ExtendedTimeSlotProps = {
            id: session.id,
            startDatetime: session.startDatetime,
            endDatetime: session.endDatetime,
            urgencySignals: getUrgencySignals(session, isAppRebrandActive),
            isDisable: isSlotDisabled(session.salesStatus),
            className: getTimeSlotClassName(
                session.salesStatus,
                isAppRebrandActive,
            ),
            sessionTextClassName: getSessionTextClassName(
                session.salesStatus,
                isAppRebrandActive,
            ),
        };

        sessionsByDuration[duration] ??= [];
        sessionsByDurationAndTimeOfDay[duration] ??= getEmptyDayPartMap();

        sessionsByDuration[duration].push(props);
        sessionsByDurationAndTimeOfDay[duration][dayPart].push(props);
    }

    return {
        sessionsByDuration,
        sessionsByDurationAndTimeOfDay,
    };
}

export const hasTimeSlots = (
    sessionsByDurationAndTimeOfDay: SessionsGroupedByTimeOfDay,
) =>
    Object.values(sessionsByDurationAndTimeOfDay).some(
        (dayPart) => Object.values(dayPart).length > 0,
    );

export const getSortedSessionsByDuration = (
    sessionsByDuration: Record<string, ExtendedTimeSlotProps[]>,
): { duration: number; sessions: ExtendedTimeSlotProps[] }[] =>
    Object.entries(sessionsByDuration)
        .map(([duration, sessions]) => ({
            duration: Number(duration),
            sessions: sessions.sort(
                (a, b) =>
                    new Date(a.startDatetime).getTime() -
                    new Date(b.startDatetime).getTime(),
            ),
        }))
        .filter(({ duration }) => !isNaN(duration) && duration >= 0)
        .sort((a, b) => a.duration - b.duration);

export const filterTabHeadersBySessionsInDayPart = (
    sessionsGroupedByTimeOfDay: SessionsGroupedByTimeOfDay,
) =>
    TAB_HEADERS.filter(
        (tab) => sessionsGroupedByTimeOfDay[tab as keyof DayPart].length,
    );

export const getDefaultActiveTab = (
    sessionsGroupedByTimeOfDay: SessionsGroupedByTimeOfDay,
) => {
    for (const tab in sessionsGroupedByTimeOfDay) {
        if (sessionsGroupedByTimeOfDay[tab as keyof DayPart].length) {
            return tab as keyof DayPart;
        }
    }
    return;
};

export function getSortedSessionsByDurationAndTimeOfDay(
    sessionsByDurationAndTimeOfDay: SessionsByDurationAndTimeOfDay,
) {
    return Object.entries(sessionsByDurationAndTimeOfDay).sort(
        ([a], [b]) => Number(a) - Number(b),
    );
}

export const getFormattedTime = (
    isoString: string,
    locale: string,
    timeZone: string,
): string => {
    if (!isoString || isNaN(Date.parse(isoString))) {
        return '';
    }
    return formatInTimeZone(parseISO(isoString), timeZone, 'h:mm aaa', {
        locale: getDateFnsLocale(locale),
    });
};

export function sortSessionsByStartTime<T extends { startDatetime: string }>(
    sessions: T[],
): T[] {
    return sessions.sort(
        (a, b) =>
            new Date(a.startDatetime).getTime() -
            new Date(b.startDatetime).getTime(),
    );
}

export function getTabForSessionId(
    selectedSessionId: string,
    timeSlotData: TimeSlot[],
    timezone: string,
) {
    const session = timeSlotData.find((slot) => slot.id === selectedSessionId);
    if (!session) {
        return;
    }

    return getSessionDayPart(session.startDatetime, timezone);
}

export const shouldShowDurationTag = (
    sessions: ExtendedTimeSlotProps[],
): boolean => {
    if (sessions.length === 0) {
        return false;
    }
    const endSessionTimes = sessions.map((session) =>
        Date.parse(session.endDatetime),
    );

    const firstSessionEndTime = endSessionTimes[0];
    const allSessionsWithSameEndTime = endSessionTimes.every(
        (endTime) => endTime === firstSessionEndTime,
    );

    // Return true (show duration) if end times are different
    // Return false (hide duration) if all end times are the same
    return !allSessionsWithSameEndTime;
};

/**
 * Combines sessions from different duration groups into their respective time sections
 */
export const combineSessionsByTimeSection = (
    sessionsByDurationAndTimeOfDay: SessionsByDurationAndTimeOfDay,
): Record<string, ExtendedTimeSlotProps[]> => {
    const flattenedTimeGroups: Record<string, ExtendedTimeSlotProps[]> =
        getEmptyDayPartMap();

    Object.values(sessionsByDurationAndTimeOfDay).forEach((durationGroup) => {
        Object.entries(durationGroup).forEach(([timeSection, sessions]) => {
            flattenedTimeGroups[timeSection] = [
                ...flattenedTimeGroups[timeSection],
                ...sessions,
            ];
        });
    });

    return flattenedTimeGroups;
};

/**
 * Sorts sessions within each time section by start time
 */
export const sortSessionsWithinTimeSections = (
    timeGroups: Record<string, ExtendedTimeSlotProps[]>,
): Record<string, ExtendedTimeSlotProps[]> => {
    const sortedTimeGroups = { ...timeGroups };

    Object.keys(sortedTimeGroups).forEach((timeSection) => {
        sortedTimeGroups[timeSection] = sortSessionsByStartTime(
            sortedTimeGroups[timeSection],
        );
    });

    return sortedTimeGroups;
};

/**
 * Flattens duration-based grouping into a single time-based grouping
 */
export const flattenDurationGrouping = (
    sessionsByDurationAndTimeOfDay: SessionsByDurationAndTimeOfDay,
): [string, Record<string, ExtendedTimeSlotProps[]>] => {
    // Step 1: Combine sessions by time section
    const combinedTimeGroups = combineSessionsByTimeSection(
        sessionsByDurationAndTimeOfDay,
    );

    // Step 2: Sort sessions within each time section
    const sortedTimeGroups = sortSessionsWithinTimeSections(combinedTimeGroups);

    return ['', sortedTimeGroups];
};

/**
 * Conditionally sorts time slots based on whether duration should be shown
 */
export const getSortedTimeSlotsBasedOnDuration = (
    sessionsByDurationAndTimeOfDay: SessionsByDurationAndTimeOfDay,
    shouldShowDuration: boolean,
): [string, Record<string, ExtendedTimeSlotProps[]>][] => {
    if (shouldShowDuration) {
        return getSortedSessionsByDurationAndTimeOfDay(
            sessionsByDurationAndTimeOfDay,
        );
    } else {
        return [flattenDurationGrouping(sessionsByDurationAndTimeOfDay)];
    }
};
